import React, { useState } from "react";

import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import backendApi from "@/api/backendApi";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export default function ProfileSummarySection({ profile }) {
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [summary, setSummary] = useState(profile.profile_summary || "");

  const updateMutation = useMutation({
    mutationFn: (newSummary) => backendApi.entities.UserProfile.updateSummary(newSummary),
    onSuccess: () => {
      queryClient.invalidateQueries(["userProfile"]);
      setIsEditing(false);
    },
  });

  const handleSave = () => {
    updateMutation.mutate(summary);
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      {/* HEADER */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-800">Profile summary</h3>

        <button
          onClick={() => setIsEditing(true)}
          className="text-blue-600 hover:underline text-sm font-medium flex items-center gap-1"
        >
          {!!profile?.profile_summary ? "Edit" : "Add"}
        </button>
      </div>

      {/* DISPLAY SUMMARY */}
      {profile.profile_summary ? (
        <p className="text-gray-600 whitespace-pre-line">
          {profile.profile_summary}
        </p>
      ) : (
        <p className="text-gray-500">
          Highlight your achievements to help employers understand your strengths.
        </p>
      )}

      {/* EDIT POPUP */}
      <Dialog open={isEditing} onOpenChange={setIsEditing}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Profile Summary</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <Textarea
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              placeholder="Write a short summary about your experience, skills, and goals..."
              className="min-h-[180px]"
            />

            <p className="text-sm text-gray-500">
              A strong summary improves your chances of getting shortlisted.
            </p>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setIsEditing(false)}>
                Cancel
              </Button>
              <Button onClick={handleSave}>Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
